import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Logo } from "../lib/brand";
import {
  BookOpen,
  Handshake,
  PenLine,
  FileText,
  Sparkles,
  Scale,
  ArrowRight,
  ShieldCheck,
  Globe2,
  Lock,
  History,
  UserCheck,
  Package,
  CheckCircle2,
} from "lucide-react";
import ProductPreview from "../components/ProductPreview";
import { JURISDICTION_LIST } from "../data/jurisdictions";

// ---------- 6 primary features (from Section 4 of the new copy) ----------
const FEATURES = [
  {
    number: "01",
    icon: Handshake,
    title: "Agreements",
    subtitle: "Good Agreements Build Better Relationships.",
    body:
      "Create, customise, and manage legal agreements using professionally structured templates — from employment contracts and NDAs to business partnerships, service agreements, and personal arrangements.",
    points: [
      "Explore templates for different legal needs.",
      "Adapt agreements to applicable jurisdictions.",
      "Collaborate with other parties.",
      "Manage revisions and document versions.",
      "Track agreement status and renewals.",
    ],
    cta: { label: "Explore Agreements", to: "/app/agreements" },
  },
  {
    number: "02",
    icon: PenLine,
    title: "Digital Signatures",
    subtitle: "An Agreement Deserves a Better Handshake.",
    body:
      "Prepare documents for electronic signing, invite participants, and keep track of the signing process end-to-end.",
    points: [
      "Request signatures with ordered or parallel signing.",
      "Manage multiple signatories and witnesses.",
      "Track pending and completed signatures in real time.",
      "Maintain signing records and audit trails.",
      "Store executed agreements securely with SHA-256 integrity.",
    ],
    footnote:
      "Signature validity and legal enforceability depend on applicable law and the signing method used.",
    cta: { label: "Start Signing", to: "/app/signatures" },
  },
  {
    number: "03",
    icon: Scale,
    title: "Case Management",
    subtitle: "Every Case Has a Story. Keep Yours Together.",
    body:
      "From an initial complaint to a final resolution, Marianne helps you manage the information that matters — case files, documents, developments, and the people involved.",
    points: [
      "Create and manage legal matters.",
      "Use structured case-intake templates.",
      "Organise case histories and timelines.",
      "Track tasks, hearings, and deadlines.",
      "Collaborate with authorised participants.",
      "Maintain records of communications and developments.",
    ],
    cta: { label: "Manage a Case", to: "/app/cases" },
  },
  {
    number: "04",
    icon: Package,
    title: "Evidence Management",
    subtitle: "The Details Matter. Preserve Them.",
    body:
      "Bring your supporting documents, photographs, recordings, correspondence, and other relevant materials into an organised evidence workspace.",
    points: [
      "Upload and categorise evidence.",
      "Connect evidence to relevant cases.",
      "Record sources and supporting context.",
      "Maintain access histories and version records.",
      "Control who can view sensitive materials.",
      "Export organised evidence collections.",
    ],
    cta: { label: "Organise Evidence", to: "/app/evidence" },
  },
  {
    number: "05",
    icon: FileText,
    title: "Legal Documents",
    subtitle: "From a Blank Page to a Better Beginning.",
    body:
      "Prepare common legal paperwork without starting from scratch. Explore structured templates for notices, declarations, applications, complaints, letters, and contracts — with guided workflows that tell you what belongs where.",
    points: [
      "Less time formatting. More time thinking.",
    ],
    cta: { label: "Explore Documents", to: "/app/templates" },
  },
  {
    number: "06",
    icon: Sparkles,
    title: "Legal Intelligence",
    subtitle: "Understand Before You Act.",
    body:
      "Complex legal language shouldn't stand between you and understanding your documents. Marianne's AI-assisted tools help you prepare — not replace your judgment.",
    points: [
      "Summarise lengthy documents.",
      "Explain unfamiliar legal terminology.",
      "Identify clauses requiring attention.",
      "Compare document revisions.",
      "Prepare questions for legal professionals.",
      "Organise relevant information for review.",
    ],
    footnote:
      "AI-generated information may contain errors and should be reviewed by a qualified professional where appropriate.",
    cta: { label: "Explore Legal Intelligence", to: "/app/learn" },
  },
];

const STEPS = [
  {
    n: "01",
    title: "Choose your legal task",
    body: "Start with an agreement, document, case, or evidence collection.",
  },
  {
    n: "02",
    title: "Create your workspace",
    body: "Use templates and guided tools to structure your information.",
  },
  {
    n: "03",
    title: "Prepare and collaborate",
    body:
      "Draft documents, invite authorised participants, request signatures, organise supporting materials.",
  },
  {
    n: "04",
    title: "Stay in control",
    body:
      "Track progress, manage deadlines, and maintain an accessible record of your legal work.",
  },
];

const AUDIENCES = [
  {
    title: "Individuals",
    body:
      "Understand agreements, prepare important documents, and organise personal legal matters.",
  },
  {
    title: "Entrepreneurs",
    body:
      "Create business agreements, manage contracts, and keep essential legal records organised.",
  },
  {
    title: "Businesses",
    body:
      "Coordinate contracts, signatures, compliance documentation, and legal matters across teams.",
  },
  {
    title: "Legal Professionals",
    body:
      "Manage clients, documents, evidence, case histories, and collaborative workflows.",
  },
  {
    title: "Organisations",
    body:
      "Build more structured, accountable, and accessible legal operations.",
  },
];

const SECURITY = [
  {
    icon: Lock,
    title: "Privacy by Design",
    body: "Your information should be handled with care.",
  },
  {
    icon: UserCheck,
    title: "Controlled Access",
    body: "Sensitive records should only be accessible to authorised participants.",
  },
  {
    icon: History,
    title: "Document Integrity",
    body: "Important changes should be traceable.",
  },
  {
    icon: ShieldCheck,
    title: "Accountability",
    body: "Legal workflows should maintain clear records of relevant actions.",
  },
  {
    icon: Package,
    title: "Data Portability",
    body: "Your documents and records should remain accessible to you.",
  },
];

const ECOSYSTEM = [
  { name: "Marianne", subtitle: "Legal Work", body: "Draft agreements, manage cases, organise evidence, and coordinate signatures." },
  { name: "Nyaya Kosa", subtitle: "Legal Knowledge", body: "Explore laws, legal frameworks, and structured legal information." },
  { name: "Calcutta", subtitle: "Intellectual Property", body: "Organise and manage patents, trademarks, copyrights, licensing, and royalties." },
  { name: "Artha", subtitle: "Business Incorporation", body: "Explore company formation and incorporation workflows across jurisdictions." },
  { name: "Lemonade", subtitle: "Financial Management", body: "Manage business finances and accounting workflows." },
];

const PRICING = [
  {
    name: "Free",
    price: "₹0",
    tagline: "For occasional users.",
    features: ["1 active matter", "Basic templates", "Claim Explorer (limited)", "Document storage allowance", "Community support"],
    cta: "Start free",
  },
  {
    name: "Individual",
    price: "₹299",
    suffix: "/ month",
    tagline: "For personal legal work.",
    features: ["10 active matters", "All templates", "Claim Explorer", "Evidence organisation", "Basic signature allowance", "Email support"],
    cta: "Choose Individual",
  },
  {
    name: "Business",
    price: "₹999",
    suffix: "/ month",
    tagline: "Freelancers & micro-businesses.",
    features: ["Unlimited matters (fair use)", "Agreement management", "25 signature requests / mo", "Obligation tracking", "Business templates", "Team: 3 users"],
    cta: "Choose Business",
    featured: true,
  },
  {
    name: "Business Pro",
    price: "₹2,999",
    suffix: "/ month",
    tagline: "Growing SMEs.",
    features: ["10 users", "100 signature requests / mo", "Approval workflows", "Clause library & custom templates", "Case management", "API allowance"],
    cta: "Choose Pro",
  },
  {
    name: "Organisation",
    price: "Custom",
    tagline: "Larger teams & legal departments.",
    features: ["SSO & advanced permissions", "API & custom integrations", "Audit controls", "Custom retention policies", "Dedicated support"],
    cta: "Talk to us",
  },
];

const FOOTER = {
  Product: ["Agreements", "Digital Signatures", "Case Management", "Evidence", "Legal Documents", "Legal Intelligence"],
  Explore: ["Features", "Templates", "Pricing", "Learn", "Security"],
  Ecosystem: ["Mahakali Tribunal", "Nyaya Kosa", "Calcutta", "Artha", "Lemonade"],
  Company: ["About", "Contact", "Privacy Policy", "Terms of Service"],
};

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-[#FAFAF8] text-[#2A0F5A]" data-testid="landing-page">
      {/* ---------------- Nav ---------------- */}
      <header className="sticky top-0 z-40 bg-[#FAFAF8]/95 backdrop-blur-sm border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="landing-brand">
            <Logo size={30} />
            <span className="font-serif text-2xl leading-none">Marianne</span>
            <span className="label-eyebrow ml-2 hidden sm:inline">by Mahakali Tribunal</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm">
            <a href="#features" className="hover:text-[#7B56C9]" data-testid="nav-link-features">Features</a>
            <a href="#how" className="hover:text-[#7B56C9]" data-testid="nav-link-how">How it works</a>
            <a href="#ecosystem" className="hover:text-[#7B56C9]" data-testid="nav-link-ecosystem">Ecosystem</a>
            <a href="#pricing" className="hover:text-[#7B56C9]" data-testid="nav-link-pricing">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm hover:text-[#7B56C9]" data-testid="landing-signin">Sign in</Link>
            <Button asChild className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs px-5">
              <Link to="/signup" data-testid="landing-get-started">Get started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* ---------------- Hero ---------------- */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-28 md:py-36">
          <div className="max-w-4xl">
            <div className="label-eyebrow text-[#C5A059] mb-8">A Legal Operating System · État de Droit</div>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-[5.5rem] tracking-tighter leading-[0.98] font-medium">
              Legal work, reimagined.<br />
              <span className="italic text-[#3D1F85]">Justice begins with clarity.</span>
            </h1>
            <p className="mt-8 text-[18px] text-[#2A0F5A]/75 max-w-2xl leading-relaxed">
              Draft agreements. Sign documents. Manage cases. Organise evidence. Protect your rights. Everything you need to navigate legal matters, thoughtfully brought together in one intelligent workspace.
            </p>
            <p className="mt-4 text-[16px] text-[#2A0F5A]/70 max-w-2xl leading-relaxed">
              From your first agreement to your most complex legal case, Marianne helps you understand, prepare, organise, and act with confidence.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
                <Link to="/signup" data-testid="hero-cta-primary">Get started for free</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="rounded-none border-[#2A0F5A] text-[#2A0F5A] hover:bg-[#2A0F5A] hover:text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
                <a href="#features" data-testid="hero-cta-secondary">Explore Marianne</a>
              </Button>
            </div>
            <p className="mt-10 label-eyebrow text-[#4A5568]">Your documents. Your cases. Your rights. One workspace.</p>
          </div>

          {/* Product preview */}
          <div className="mt-16 md:mt-20">
            <ProductPreview />
          </div>

          {/* Jurisdiction chips */}
          <div className="mt-14 flex flex-wrap items-center gap-3" data-testid="jurisdiction-chips">
            <span className="label-eyebrow text-[#4A5568] mr-1">Localised for</span>
            {JURISDICTION_LIST.map((j) => (
              <Link
                key={j.slug}
                to={`/j/${j.slug}`}
                className="px-3 py-1.5 text-xs border border-[#E8E3F0] bg-white hover:border-[#2A0F5A] inline-flex items-center gap-2 transition-colors"
                data-testid={`chip-${j.slug}`}
              >
                <span>{j.flag}</span><span className="tracking-wider uppercase text-[10px]">{j.name}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Problem ---------------- */}
      <section className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-5 gap-12">
          <div className="lg:col-span-2">
            <div className="label-eyebrow mb-4">The problem</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              The law shouldn't be <span className="italic text-[#3D1F85]">difficult to access.</span>
            </h2>
          </div>
          <div className="lg:col-span-3 text-[16px] leading-[1.8] text-[#2A0F5A]/85 space-y-4">
            <p>Legal systems are essential to society. Yet navigating them can be expensive, intimidating, time-consuming, and unnecessarily complicated.</p>
            <ul className="space-y-2 pl-5 border-l-2 border-[#C5A059]/40 text-[15px]">
              <li>An agreement shouldn't require hours of uncertainty.</li>
              <li>Important evidence shouldn't disappear into scattered folders.</li>
              <li>Managing a legal case shouldn't mean losing track of documents, deadlines, and communications.</li>
              <li>Understanding your rights shouldn't be a privilege reserved for those who can afford expensive legal assistance.</li>
            </ul>
            <p className="pt-4 text-[17px]">
              Marianne was created to make everyday legal work simpler — not by replacing legal professionals or institutions, but by giving people <b>better tools to work with them</b>.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- Introducing ---------------- */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-28 text-center">
          <div className="label-eyebrow text-[#C5A059] mb-6">Introducing Marianne</div>
          <h2 className="font-serif text-5xl md:text-6xl tracking-tighter leading-[1] max-w-4xl mx-auto">
            Your legal world. <span className="italic text-[#3D1F85]">Beautifully organised.</span>
          </h2>
          <p className="mt-8 text-[17px] leading-[1.8] text-[#2A0F5A]/80 max-w-3xl mx-auto">
            One workspace for the documents you create, the agreements you enter, the cases you manage, and the rights you seek to protect. Whether you're an individual, entrepreneur, organisation, or legal professional, Marianne brings structure and intelligence to your legal work.
          </p>
          <p className="mt-6 font-serif italic text-xl text-[#3D1F85]">
            One place to prepare. One place to collaborate. One place to move forward.
          </p>
        </div>
      </section>

      {/* ---------------- Features ---------------- */}
      <section id="features" className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-16">
            <div className="label-eyebrow mb-4">Features</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Everything legal. <span className="italic text-[#3D1F85]">One workspace.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-x-10 gap-y-14">
            {FEATURES.map((f) => {
              const Icon = f.icon;
              return (
                <article key={f.number} className="group" data-testid={`feature-${f.number}`}>
                  <div className="flex items-baseline gap-4 mb-3">
                    <span className="label-eyebrow text-[#C5A059] font-mono">{f.number}</span>
                    <Icon size={16} className="text-[#C5A059]" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-serif text-3xl tracking-tight leading-tight mb-1">{f.title}</h3>
                  <div className="font-serif italic text-lg text-[#3D1F85] mb-4">{f.subtitle}</div>
                  <p className="text-[15px] leading-[1.75] text-[#2A0F5A]/85 mb-4">{f.body}</p>
                  {f.points.length > 0 && (
                    <ul className="space-y-1.5 text-[14px] text-[#2A0F5A]/80 mb-4">
                      {f.points.map((p, i) => (
                        <li key={i} className="flex gap-2.5">
                          <CheckCircle2 size={13} className="mt-1 text-[#C5A059] shrink-0" strokeWidth={1.75} />{p}
                        </li>
                      ))}
                    </ul>
                  )}
                  {f.footnote && <p className="text-xs italic text-[#4A5568] mb-4">{f.footnote}</p>}
                  <Link to={f.cta.to} className="inline-flex items-center gap-1.5 label-eyebrow text-[#2A0F5A] hover:gap-2.5 transition-all">
                    {f.cta.label} <ArrowRight size={11} />
                  </Link>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------------- How it works ---------------- */}
      <section id="how" className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="grid lg:grid-cols-5 gap-12">
            <div className="lg:col-span-2">
              <div className="label-eyebrow mb-4">How it works</div>
              <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
                From uncertainty to <span className="italic text-[#3D1F85]">organisation.</span>
              </h2>
              <p className="mt-6 text-[15px] leading-[1.8] text-[#2A0F5A]/80">
                Your legal matters deserve a clear beginning, a structured process, and a reliable record.
              </p>
              <Button asChild className="mt-8 rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs">
                <Link to="/signup" data-testid="how-cta">Create your workspace →</Link>
              </Button>
            </div>
            <ol className="lg:col-span-3 divide-y divide-[#E8E3F0] border-y border-[#E8E3F0]">
              {STEPS.map((s) => (
                <li key={s.n} className="py-6 flex gap-6" data-testid={`step-${s.n}`}>
                  <span className="label-eyebrow text-[#C5A059] font-mono text-sm pt-1.5 shrink-0">{s.n}</span>
                  <div>
                    <div className="font-serif text-xl mb-1">{s.title}</div>
                    <p className="text-[14px] leading-[1.75] text-[#2A0F5A]/80">{s.body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ---------------- Who is Marianne for? ---------------- */}
      <section className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <div className="label-eyebrow mb-4">Who is Marianne for?</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Built for people. <span className="italic text-[#3D1F85]">Designed for possibilities.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-x-6 gap-y-8">
            {AUDIENCES.map((a) => (
              <div key={a.title} data-testid={`audience-${a.title.toLowerCase()}`}>
                <div className="font-serif text-xl mb-2 pb-2 border-b border-[#C5A059]/40 inline-block">{a.title}</div>
                <p className="text-[14px] leading-[1.7] text-[#2A0F5A]/80">{a.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-14 font-serif italic text-xl text-[#3D1F85] max-w-3xl">
            Whether you're protecting a personal interest or managing an organisation, Marianne gives your legal work a place to belong.
          </p>
        </div>
      </section>

      {/* ---------------- Global ---------------- */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-5 gap-12 items-center">
          <div className="lg:col-span-3">
            <div className="label-eyebrow mb-4">Global legal workspace</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Different countries. Different laws. <span className="italic text-[#3D1F85]">One workspace.</span>
            </h2>
            <div className="mt-6 text-[15px] leading-[1.8] text-[#2A0F5A]/85 space-y-3">
              <p>The world is connected. Its legal systems are not always so simple.</p>
              <p>Marianne is designed with international legal work in mind. Discover jurisdiction-specific templates, organise cross-border agreements, and manage documents involving multiple countries.</p>
              <p>As jurisdictional coverage expands, Marianne aims to make navigating different legal systems more understandable and manageable.</p>
            </div>
            <p className="mt-6 font-serif italic text-lg text-[#3D1F85]">
              Built with a global perspective. Grounded in local law.
            </p>
            <p className="mt-3 text-xs italic text-[#4A5568]">Template availability and legal requirements vary by jurisdiction.</p>
          </div>
          <div className="lg:col-span-2 flex items-center justify-center">
            <Globe2 size={200} strokeWidth={0.75} className="text-[#C5A059]/30" />
          </div>
        </div>
      </section>

      {/* ---------------- Security ---------------- */}
      <section className="border-b border-[#E8E3F0] bg-[#0B132B] text-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <div className="label-eyebrow text-[#C5A059] mb-4">Security & trust</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Your legal matters are <span className="italic text-[#C5A059]">your business.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-[1.8] text-[#FAFAF8]/80">
              Legal information can be among the most sensitive information a person or organisation possesses. Marianne is designed around the principles of privacy, confidentiality, accountability, and user control.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-x-6 gap-y-10">
            {SECURITY.map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.title} data-testid={`security-${s.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                  <Icon size={22} strokeWidth={1.5} className="text-[#C5A059] mb-4" />
                  <div className="font-serif text-xl mb-2">{s.title}</div>
                  <p className="text-[14px] leading-[1.7] text-[#FAFAF8]/75">{s.body}</p>
                </div>
              );
            })}
          </div>
          <p className="mt-16 font-serif italic text-2xl text-[#C5A059] text-center">
            Trust is not a slogan. It is a responsibility.
          </p>
        </div>
      </section>

      {/* ---------------- Ecosystem ---------------- */}
      <section id="ecosystem" className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <div className="label-eyebrow mb-4">The Marianne ecosystem</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Part of <span className="italic text-[#3D1F85]">something greater.</span>
            </h2>
            <p className="mt-6 text-[15px] leading-[1.8] text-[#2A0F5A]/80">
              Marianne is part of Mahakali Tribunal — an ecosystem dedicated to making intelligence, justice, and wealth more accessible.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 border border-[#E8E3F0]">
            {ECOSYSTEM.map((p, i) => (
              <div
                key={p.name}
                className={`p-6 ${i !== ECOSYSTEM.length - 1 ? "md:border-r border-b md:border-b-0 border-[#E8E3F0]" : "border-b md:border-b-0 border-[#E8E3F0]"}`}
                data-testid={`ecosystem-${p.name.toLowerCase()}`}
              >
                <div className="font-serif text-2xl mb-1">{p.name}</div>
                <div className="label-eyebrow text-[#C5A059] mb-3">{p.subtitle}</div>
                <p className="text-[13px] leading-[1.7] text-[#2A0F5A]/80">{p.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-12 font-serif italic text-xl text-[#3D1F85]">
            Connected tools. Clearer workflows. Greater independence.
          </p>
        </div>
      </section>

      {/* ---------------- Philosophy ---------------- */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-5xl mx-auto px-6 py-28 text-center">
          <div className="label-eyebrow text-[#C5A059] mb-6">Our philosophy</div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
            Justice shouldn't begin with a <span className="italic text-[#3D1F85]">financial barrier.</span>
          </h2>
          <div className="mt-10 text-[16px] leading-[1.9] text-[#2A0F5A]/85 space-y-4 max-w-3xl mx-auto text-left">
            <p>We believe people should have access to tools that help them understand agreements, document facts, organise legal matters, and participate more confidently in legal processes.</p>
            <p>We believe legal professionals deserve better technology.</p>
            <p>We believe businesses should be able to manage their legal responsibilities without unnecessary complexity.</p>
            <p>And we believe technology should make the law more understandable — not more intimidating.</p>
            <p className="pt-4 font-serif italic text-xl text-[#3D1F85] text-center">
              Marianne exists to make legal work more accessible, organised, and humane.
            </p>
          </div>
        </div>
      </section>

      {/* ---------------- Pricing ---------------- */}
      <section id="pricing" className="border-b border-[#E8E3F0] bg-white">
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="max-w-3xl mb-14">
            <div className="label-eyebrow mb-4">Pricing</div>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05]">
              Fair pricing. <span className="italic text-[#3D1F85]">Honest limits.</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 border border-[#E8E3F0]">
            {PRICING.map((p, i) => (
              <div
                key={p.name}
                className={`p-6 flex flex-col ${p.featured ? "bg-[#FAFAF8]" : ""} ${i !== PRICING.length - 1 ? "md:border-r border-b md:border-b-0 border-[#E8E3F0]" : "border-b md:border-b-0 border-[#E8E3F0]"}`}
                data-testid={`pricing-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              >
                <div className="label-eyebrow mb-2">{p.name}</div>
                <div className="font-serif text-3xl leading-none">
                  {p.price}
                  {p.suffix && <span className="text-sm font-sans text-[#4A5568] ml-1">{p.suffix}</span>}
                </div>
                <p className="text-xs text-[#4A5568] mt-2 mb-5">{p.tagline}</p>
                <ul className="space-y-1.5 text-[13px] text-[#2A0F5A]/85 flex-1">
                  {p.features.map((f) => (
                    <li key={f} className="flex gap-2"><CheckCircle2 size={11} className="mt-1 text-[#C5A059] shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button asChild variant={p.featured ? "default" : "outline"} className={`mt-6 rounded-none uppercase tracking-widest text-xs ${p.featured ? "bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8]" : "border-[#2A0F5A] text-[#2A0F5A]"}`}>
                  <Link to="/signup">{p.cta}</Link>
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ---------------- Final CTA ---------------- */}
      <section className="border-b border-[#E8E3F0]">
        <div className="max-w-5xl mx-auto px-6 py-28 text-center">
          <div className="space-y-2 mb-10">
            <div className="font-serif italic text-2xl text-[#3D1F85]">Your rights matter.</div>
            <div className="font-serif italic text-2xl text-[#3D1F85]">Your agreements matter.</div>
            <div className="font-serif italic text-2xl text-[#3D1F85]">Your story matters.</div>
          </div>
          <h2 className="font-serif text-4xl md:text-5xl tracking-tight leading-[1.05] max-w-3xl mx-auto">
            Give your legal work the clarity, structure, and attention <span className="italic text-[#3D1F85]">it deserves.</span>
          </h2>
          <p className="mt-6 text-[15px] text-[#2A0F5A]/75">
            Create agreements. Manage cases. Preserve evidence. Sign documents.
          </p>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
            <Button asChild size="lg" className="rounded-none bg-[#2A0F5A] hover:bg-[#4A2680] text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
              <Link to="/signup" data-testid="final-cta-primary">Get started for free</Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="rounded-none border-[#2A0F5A] text-[#2A0F5A] hover:bg-[#2A0F5A] hover:text-[#FAFAF8] uppercase tracking-widest text-xs px-7 py-6">
              <a href="#features" data-testid="final-cta-secondary">Explore features</a>
            </Button>
          </div>
          <p className="mt-16 font-serif italic text-3xl text-[#C5A059]">Legal work, made human.</p>
        </div>
      </section>

      {/* ---------------- Footer ---------------- */}
      <footer className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid md:grid-cols-5 gap-10 mb-12">
            <div className="md:col-span-1">
              <div className="flex items-center gap-2.5 mb-3">
                <Logo size={26} />
                <span className="font-serif text-xl">Marianne</span>
              </div>
              <div className="label-eyebrow mb-2">by Mahakali Tribunal</div>
              <p className="text-xs text-[#4A5568] italic font-serif mt-3">Justice begins with clarity.</p>
            </div>
            {Object.entries(FOOTER).map(([title, links]) => (
              <div key={title}>
                <div className="label-eyebrow mb-4">{title}</div>
                <ul className="space-y-2 text-sm">
                  {links.map((l) => (
                    <li key={l}><a href="#" className="text-[#2A0F5A]/85 hover:text-[#7B56C9]">{l}</a></li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="pt-8 border-t border-[#E8E3F0] text-xs text-[#4A5568] leading-relaxed">
            <p className="mb-3 italic">
              Marianne provides legal workflow and information tools. It is not a law firm and does not independently provide legal representation. AI-generated content and templates are not substitutes for advice from a qualified legal professional. Availability and functionality vary by jurisdiction.
            </p>
            <p>© 2026 Mahakali Tribunal. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
